// SubmitOptionsBuilderPage.jsx
// -----------------------------------------------------------------------------
// Renders the 5 submission-action icons, opens the settings side-panel,
// shows live previews, etc.
//
// LOCKED ACTION UX
// ----------------
// • `redirect`, `sendEmail`, `displayToast` are “locked” (plan-upgrade gates)
// • A *dark* semi-transparent overlay (rgba 0,0,0,0.55) + <LockOutlinedIcon/>
//   is drawn *over* the button without altering its size/layout.
// • Buttons stay “enabled” – the click is blocked in `handleActionClick()`,
//   which preserves normal visual styling and prevents shrink.
//
// Wrapped in an explanatory <Tooltip/>.
// -----------------------------------------------------------------------------

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  Tooltip,
} from '@mui/material';
import LockOutlinedIcon          from '@mui/icons-material/LockOutlined';
import OpenInNewOutlinedIcon     from '@mui/icons-material/OpenInNewOutlined';
import MailOutlineOutlinedIcon   from '@mui/icons-material/MailOutlineOutlined';
import CallToActionOutlinedIcon  from '@mui/icons-material/CallToActionOutlined';
import MessageOutlinedIcon       from '@mui/icons-material/MessageOutlined';
import TableChartOutlinedIcon    from '@mui/icons-material/TableChartOutlined';

import SidePanel                     from '../components/FormSettings/SidePanel';
import TopBarFieldSettings           from '../components/FormSettings/FormDesignLayout/FieldSettingsUI/TopBarFieldSettings';
import FormSubmitOptionsSettings     from '../components/FormSettings/FormSubmitSettings/FormSubmitOptionsSettings';
import FormSubmitSettingsPreviews    from '../components/FormSettings/FormSubmitSettings/FormSubmitSettingsPreviews';
import DisplayCardPreview            from '../components/FormSettings/FormSubmitSettings/DisplayCardPreview';

import { useFormSubmissionSettings } from '../contexts/FormSubmissionSettingsContext';
import { useFormLayout }             from '../contexts/FormLayoutContext';
import { useFieldEdit }              from '../contexts/FieldEditContext';

// ────────────────────────────────────────────────────────────────────────────
// 1) CONSTANTS – which actions are locked
// ────────────────────────────────────────────────────────────────────────────
const LOCKED_ACTIONS = ['redirect', 'sendEmail', 'displayToast'];

/* --------------------------------------------------------------------------
 * MAIN COMPONENT
 * ----------------------------------------------------------------------- */
const SubmitOptionsBuilderPage = () => {
  // ----------------------------------------------------------------------
  // CONTEXT HOOKS
  // ----------------------------------------------------------------------
  const { layoutConfig, fieldsMetadata: layoutFM } = useFormLayout();
  const { submissionSettings, updateSettings }     = useFormSubmissionSettings();
  const { fieldsMetadata: editFM, isFieldChangesReady } = useFieldEdit();

  // ----------------------------------------------------------------------
  // LOCAL STATE
  // ----------------------------------------------------------------------
  const [selectedAction, setSelectedAction] = useState('');
  const [activePreview,  setActivePreview]  = useState('');
  const [showSettings,   setShowSettings]   = useState(false);
  const [editorTempContent, setEditorTempContent] = useState(null);
  const [combinedFM, setCombinedFM] = useState([]);

  // ----------------------------------------------------------------------
  // COMBINE layout + live-edit field-metadata
  // ----------------------------------------------------------------------
  useEffect(() => {
    if (!isFieldChangesReady) return;
    const merged = Object.values(layoutConfig).flat().map((f) => {
      const meta = editFM?.[f.id] || layoutFM?.[f.id] || {};
      return {
        id:    f.id,
        label: meta.label || f.label || f.id,
        ufi:   meta.ufi,
        type:  f.type || meta.type,
        placeholder: f.placeholder || meta.placeholder,
      };
    });
    setCombinedFM(merged);
    console.log('[SubmitOptionsBuilderPage] merged fieldsMetadata:', merged);
  }, [layoutConfig, editFM, layoutFM, isFieldChangesReady]);

  // ----------------------------------------------------------------------
  // Auto-select the first *enabled* action (one-time on load)
  // ----------------------------------------------------------------------
  useEffect(() => {
    if (selectedAction || !submissionSettings) return;
    const firstEnabled = Object.keys(submissionSettings).find(
      (k) => submissionSettings[k]?.enabled
    );
    if (firstEnabled) {
      setSelectedAction(firstEnabled);
      setActivePreview(firstEnabled);
      console.log('[SubmitOptionsBuilderPage] auto-selected action:', firstEnabled);
    }
  }, [submissionSettings, selectedAction]);

  // ----------------------------------------------------------------------
  // CLICK HANDLER — blocks if locked
  // ----------------------------------------------------------------------
  const handleActionClick = useCallback((action) => {
    if (LOCKED_ACTIONS.includes(action)) {
      console.log(`[SubmitOptionsBuilderPage] '${action}' is locked – upgrade required.`);
      return;
    }
    setSelectedAction(action);
    setActivePreview(action);
    setShowSettings(true);
  }, []);

  // ----------------------------------------------------------------------
  // DONE (save) HANDLER
  // ----------------------------------------------------------------------
  const handleDoneClick = useCallback(() => {
    const current = submissionSettings[selectedAction];
    if (current) {
      updateSettings(selectedAction, {
        ...current,
        customMessage: editorTempContent || current.customMessage,
        ufi: current.ufi,
      });
    }
    setShowSettings(false);
    setSelectedAction('');
  }, [selectedAction, submissionSettings, editorTempContent, updateSettings]);

  // ----------------------------------------------------------------------
  // Receive editor content (quill delta)
  // ----------------------------------------------------------------------
  const updateEditorContent = useCallback((content) => {
    let parsed = [{ insert: '' }];
    try {
      parsed = Array.isArray(content) ? content : JSON.parse(content);
      if (!Array.isArray(parsed)) throw new Error('customMessage must be an array');
    } catch (err) {
      console.error('[SubmitOptionsBuilderPage] invalid editor content:', err);
    }
    setEditorTempContent(parsed);
  }, []);

  // ----------------------------------------------------------------------
  // ACTION BUTTON DEFINITIONS
  // ----------------------------------------------------------------------
  const actionButtons = [
    { action: 'redirect',              label: 'Redirect',      icon: <OpenInNewOutlinedIcon /> },
    { action: 'sendEmail',             label: 'Send Email',    icon: <MailOutlineOutlinedIcon /> },
    { action: 'displayToast',          label: 'Display Toast', icon: <MessageOutlinedIcon /> },
    { action: 'displayInCard',         label: 'Display Card',  icon: <CallToActionOutlinedIcon /> },
    { action: 'addToSubmissionsTable', label: 'Display Table', icon: <TableChartOutlinedIcon /> },
  ];

  // ----------------------------------------------------------------------
  // BUILD THE SIDE-PANEL CONFIG
  // ----------------------------------------------------------------------
  const submitOptionsSection = useMemo(
    () => [
      {
        noAccordion: true,
        noPadding:   showSettings,
        items: [
          {
            element: !showSettings ? (
              /* ==========================================================
               *  GRID WITH FIVE ACTION BUTTONS
               * ======================================================= */
              <Box
                sx={{
                  display:        'flex',
                  flexWrap:       'wrap',
                  justifyContent: 'space-between',
                }}
              >
                {actionButtons.map((btn) => {
                  const isLocked  = LOCKED_ACTIONS.includes(btn.action);
                  const isActive  = activePreview === btn.action;

                  const BtnEl = (
                    <Button
                      key={btn.action}
                      onClick={() => handleActionClick(btn.action)}
                      variant={isActive ? 'contained' : 'outlined'}
                      sx={{
                        position:      'relative',
                        width:         'calc(50% - 8px)',
                        height:        100,
                        m:             '4px 4px',
                        py:            0,
                        flexDirection: 'column',
                        textTransform: 'none',
                        borderColor:   isActive ? 'primary.main' : 'grey.400',
                        backgroundColor: isActive ? 'primary.main' : 'transparent',
                        color:         isActive ? 'primary.contrastText' : 'text.primary',
                        '&:hover': {
                          backgroundColor: isActive ? 'primary.dark' : 'transparent',
                          borderColor:     isActive ? 'primary.dark' : 'grey.400',
                        },
                      }}
                    >
                      {btn.icon}
                      <Typography variant="caption" sx={{ mt: 1 }}>
                        {btn.label}
                      </Typography>

                      {/* LOCK OVERLAY – dark grey with light icon */}
                      {isLocked && (
                        <Box
                          sx={{
                            position:      'absolute',
                            inset:         0,
                            bgcolor:       'rgba(0,0,0,0.55)',     // darker mask
                            display:       'flex',
                            alignItems:    'center',
                            justifyContent:'center',
                            borderRadius:  1,
                            pointerEvents:'none',
                          }}
                        >
                          <LockOutlinedIcon sx={{ fontSize: 30, color: 'grey.100' }} />
                        </Box>
                      )}
                    </Button>
                  );

                  return isLocked ? (
                    <Tooltip
                      key={btn.action}
                      title="Upgrade your plan to unlock this feature"
                      arrow
                      placement="top"
                    >
                      {BtnEl}
                    </Tooltip>
                  ) : (
                    BtnEl
                  );
                })}
              </Box>
            ) : (
              /* ==========================================================
               *  SETTINGS SIDE-PANEL
               * ======================================================= */
              <>
                <TopBarFieldSettings onDone={handleDoneClick} onNavigateBack={handleDoneClick} />
                <Box p={2}>
                  <FormSubmitOptionsSettings
                    selectedAction={selectedAction}
                    onSave={() => {}}
                    onNavigateBack={handleDoneClick}
                  />
                </Box>
              </>
            ),
          },
        ],
      },
    ],
    [showSettings, activePreview, handleActionClick, handleDoneClick, selectedAction]
  );

  // ----------------------------------------------------------------------
  //  RENDER
  // ----------------------------------------------------------------------
  return (
    <Box display="flex" flexDirection="column" height="100vh">
      <Box 
        display="flex" 
        height="100%" 
        flexGrow={1}
        sx={{ overflow: 'hidden' }}  // 🆕 keep document from scrolling; panes scroll independently
      >
        <SidePanel
          sections={submitOptionsSection}
          onBack={() => console.log('[SubmitOptionsBuilderPage] side-panel back')}
        />

        {/* --------------- LIVE PREVIEW PANE ----------------------------- */}
        <Box
          flex={1}
          sx={{
            minWidth: 0,
            bgcolor:  '#f0f0f0',
            pt:       3,
            overflowY: 'auto',  // 🆕 right-hand preview gets its own vertical scrollbar
            minHeight: 0,       // 🆕 important for flex + overflow
          }}
        >
          {activePreview === 'displayInCard' && isFieldChangesReady ? (
            <DisplayCardPreview
              builderMode
              onUpdateEditorContent={updateEditorContent}
              fieldsMetadata={combinedFM}
            />
          ) : (
            <FormSubmitSettingsPreviews selectedAction={activePreview} />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default SubmitOptionsBuilderPage;
